import { buildFullNavTreeData } from 'navigation/rosieNavTreeData';
import { isSafeInternalPath } from 'utils/postLoginRedirect';
import {
  DEFAULT_DENIED_REDIRECT,
  ROUTE_ACCESS_RULES,
  STAFF_FALLBACK_RULE,
} from './routeAccess.config';
import { pathPrefixFromHref } from './pathPrefix.util';
import type { RosiePermission } from './permissions.types';
import type { RouteAccessRule } from './routeAccess.types';
import {
  ROSIE_SESSION_USER_TYPES,
  type RosieSessionUserType,
} from './rosieUserTypes';

const NAV_TREE_YEAR = new Date().getFullYear().toString();

export function normalizePathname(pathname: string): string {
  if (!pathname) {
    return '/';
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/**
 * Longest pathPrefix wins. Rules with empty pathPrefix are the staff default.
 */
export function matchRuleForPath(pathname: string): RouteAccessRule {
  const path = pathPrefixFromHref(normalizePathname(pathname));
  const explicit = ROUTE_ACCESS_RULES.filter(
    r => r.pathPrefix.length > 0,
  ).sort((a, b) => b.pathPrefix.length - a.pathPrefix.length);
  const matched = explicit.find(
    rule =>
      path === rule.pathPrefix || path.startsWith(`${rule.pathPrefix}/`),
  );
  return matched ?? STAFF_FALLBACK_RULE;
}

export function canUserAccessPath(
  userType: string | undefined | null,
  pathname: string,
): boolean {
  if (!userType) {
    return false;
  }
  const rule = matchRuleForPath(pathname);
  return (rule.allowedUserTypes as readonly string[]).includes(userType);
}

export function getDeniedRedirect(rule: RouteAccessRule): string {
  return rule.deniedRedirect ?? DEFAULT_DENIED_REDIRECT;
}

export function getRuleById(ruleId: string): RouteAccessRule {
  const rule = ROUTE_ACCESS_RULES.find(r => r.id === ruleId);
  if (!rule) {
    throw new Error(`Unknown route access rule: ${ruleId}`);
  }
  return rule;
}

/** Map `some-rule-id:read` → rule with id `some-rule-id`, if it exists. */
export function getRuleForPermission(
  permission: RosiePermission,
): RouteAccessRule | undefined {
  if (permission === '*') {
    return undefined;
  }
  const i = permission.lastIndexOf(':');
  if (i <= 0) {
    return undefined;
  }
  const id = permission.slice(0, i);
  return ROUTE_ACCESS_RULES.find(r => r.id === id);
}

export function userMayAccessRule(
  userType: string | undefined | null,
  rule: RouteAccessRule,
): boolean {
  if (!userType) {
    return false;
  }
  return (rule.allowedUserTypes as readonly string[]).includes(userType);
}

export function isRosieSessionUserType(
  value: string,
): value is RosieSessionUserType {
  return (ROSIE_SESSION_USER_TYPES as readonly string[]).includes(value);
}

export type NavTreeNodeLike = {
  id?: string;
  to?: string;
  children?: NavTreeNodeLike[];
};

/**
 * Drops nav leaves the user cannot access (same rules as route guards).
 */
export function filterNavTreeForUser<T extends NavTreeNodeLike>(
  userType: string | null | undefined,
  nodes: T[],
): T[] {
  if (!userType) {
    return [];
  }
  return nodes
    .map(node => {
      if (node.children?.length) {
        const children = filterNavTreeForUser(userType, node.children);
        if (children.length === 0) {
          return null;
        }
        return { ...node, children };
      }
      if (node.to) {
        if (!canUserAccessPath(userType, pathPrefixFromHref(node.to))) {
          return null;
        }
        return node;
      }
      if (node.id) {
        try {
          const rule = getRuleById(node.id);
          if (!userMayAccessRule(userType, rule)) {
            return null;
          }
        } catch {
          return null;
        }
      }
      return node;
    })
    .filter((n): n is T => n !== null);
}

/**
 * Filters nav leaves using the permission-aware `can` function from usePermissions().
 * Each leaf's path is matched to its rule, then rule.readPermission is checked.
 * Use this instead of filterNavTreeForUser when you have the can() function available
 * (e.g. inside a React hook) so that per-user permission overrides are respected.
 */
export function filterNavTreeByPermissions<T extends NavTreeNodeLike>(
  nodes: T[],
  can: (permission: RosiePermission) => boolean,
): T[] {
  return nodes
    .map(node => {
      if (node.children?.length) {
        const children = filterNavTreeByPermissions(node.children, can);
        if (children.length === 0) {
          return null;
        }
        return { ...node, children };
      }
      if (node.to) {
        const rule = matchRuleForPath(pathPrefixFromHref(node.to));
        if (!can(rule.readPermission)) {
          return null;
        }
        return node;
      }
      if (node.id) {
        try {
          const rule = getRuleById(node.id);
          if (!can(rule.readPermission)) {
            return null;
          }
        } catch {
          return null;
        }
      }
      return node;
    })
    .filter((n): n is T => n !== null);
}

function pathnameFromInternalPath(path: string): string {
  return path.split('?')[0].split('#')[0];
}

function isDashboardLandingPath(pathname: string): boolean {
  return pathnameFromInternalPath(pathname) === DEFAULT_DENIED_REDIRECT;
}

function findFirstNavLeafPath(nodes: NavTreeNodeLike[]): string | null {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.children?.length) {
      const childPath = findFirstNavLeafPath(node.children);
      if (childPath) {
        return childPath;
      }
    } else if (node.to) {
      return node.to;
    }
  }
  return null;
}

/** True when `can()` grants read on the route rule for `pathname`. */
export function canAccessPathByPermission(
  can: (permission: RosiePermission) => boolean,
  pathname: string,
): boolean {
  const rule = matchRuleForPath(normalizePathname(pathname));
  return can(rule.readPermission);
}

/** First sidebar nav destination the user may open (nav order). */
export function getFirstAccessibleNavPath(
  can: (permission: RosiePermission) => boolean,
  nodes?: NavTreeNodeLike[],
): string | null {
  const tree = nodes ?? buildFullNavTreeData(NAV_TREE_YEAR);
  const filtered = filterNavTreeByPermissions(tree, can);
  return findFirstNavLeafPath(filtered);
}

/**
 * Pick the first preferred path the user may access, else first nav leaf,
 * else `/dashboard` (authenticated shell when nothing is granted).
 *
 * `/dashboard` is never chosen from `preferredPaths` — it is only the empty-access
 * fallback so users with real route permissions land on an actual feature page.
 */
export function resolveAccessibleLandingPath(
  can: (permission: RosiePermission) => boolean,
  preferredPaths: string[] = [],
): string {
  for (let i = 0; i < preferredPaths.length; i += 1) {
    const path = preferredPaths[i];
    const pathname = pathnameFromInternalPath(path);
    if (
      isSafeInternalPath(pathname) &&
      !isDashboardLandingPath(pathname) &&
      canAccessPathByPermission(can, pathname)
    ) {
      return path;
    }
  }

  return getFirstAccessibleNavPath(can) ?? DEFAULT_DENIED_REDIRECT;
}
